import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  Card,
  CardBody,
  Code,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
  useDisclosure,
} from '@chakra-ui/react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FiCopy, FiExternalLink, FiEye } from 'react-icons/fi'
import { DropdownSelect } from '@/components/common/DropdownSelect'
import { RefreshIconButton } from '@/components/common/RefreshIconButton'
import { TestnetOnlyNotice } from '@/components/common/TestnetOnlyNotice'
import { useNetworkEnvironment } from '@/hooks/useNetworkEnvironment'
import {
  useTestnetFaucetClaims,
  useTestnetFaucetItems,
  useTestnetFaucetSummary,
} from '@/hooks/useTestnetFaucetMonitoringData'
import { useToast } from '@/hooks/useToast'
import type {
  AdminTestnetFaucetClaim,
  TestnetFaucetClaimStatus,
  TestnetFaucetPackageType,
} from '@/types'
import {
  formatDate,
  formatTime,
  getExplorerAddressUrl,
  getExplorerTxUrl,
  truncateHash,
} from '@/utils/formatUtils'

const DEFAULT_PAGE_SIZE = 20
const PAGE_SIZE_OPTIONS = [20, 50, 100]

const CLAIM_STATUSES: TestnetFaucetClaimStatus[] = [
  'pending',
  'queued',
  'processing',
  'success',
  'partial_success',
  'failed',
]

const PACKAGE_TYPES: TestnetFaucetPackageType[] = ['evm', 'solana']

function claimStatusColor(status: TestnetFaucetClaimStatus): string {
  if (status === 'success') return 'green'
  if (status === 'partial_success') return 'orange'
  if (status === 'failed') return 'red'
  if (status === 'processing') return 'blue'
  return 'gray'
}

function itemStatusColor(status: string): string {
  if (status === 'confirmed') return 'green'
  if (status === 'broadcast' || status === 'processing') return 'blue'
  if (status === 'failed') return 'red'
  return 'gray'
}

function DateTimeStack({ value }: { value: string }) {
  return (
    <VStack align="start" spacing={0}>
      <Text fontSize="xs" whiteSpace="nowrap">{formatDate(value)}</Text>
      <Text fontSize="xs" color="gray.500" whiteSpace="nowrap">{formatTime(value)}</Text>
    </VStack>
  )
}

type TestnetFaucetMonitorProps = {
  isActive: boolean
}

export function TestnetFaucetMonitor({ isActive }: TestnetFaucetMonitorProps) {
  const { t } = useTranslation()
  const toast = useToast()
  const { currentEnv } = useNetworkEnvironment()
  const isTestnet = currentEnv === 'testnet'
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [claimStatus, setClaimStatus] = useState('')
  const [packageType, setPackageType] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [recipientAddress, setRecipientAddress] = useState('')
  const [selectedClaim, setSelectedClaim] = useState<AdminTestnetFaucetClaim | null>(null)
  const details = useDisclosure()

  const summaryQuery = useTestnetFaucetSummary({ enabled: isActive && isTestnet })
  const claimsQuery = useTestnetFaucetClaims(
    {
      page,
      limit: pageSize,
      status: claimStatus || undefined,
      packageType: packageType || undefined,
      userEmail: userEmail.trim() || undefined,
      recipientAddress: recipientAddress.trim() || undefined,
    },
    { enabled: isActive && isTestnet }
  )
  const itemsQuery = useTestnetFaucetItems(
    selectedClaim?.id ?? null,
    { enabled: isActive && isTestnet && details.isOpen }
  )

  if (!isTestnet) {
    return <TestnetOnlyNotice />
  }

  const statusOptions = [
    { value: '', label: t('common.all') },
    ...CLAIM_STATUSES.map(status => ({
      value: status,
      label: t(`monitoring.testnetFaucet.statuses.${status}`),
    })),
  ]
  const packageOptions = [
    { value: '', label: t('common.all') },
    ...PACKAGE_TYPES.map(value => ({
      value,
      label: t(`monitoring.testnetFaucet.packages.${value}`),
    })),
  ]
  const pageSizeOptions = PAGE_SIZE_OPTIONS.map(size => ({
    value: String(size),
    label: String(size),
  }))

  const handleRefresh = async () => {
    const refreshes: Array<Promise<unknown>> = [
      summaryQuery.refetch(),
      claimsQuery.refetch(),
    ]
    if (selectedClaim && details.isOpen) {
      refreshes.push(itemsQuery.refetch())
    }
    await Promise.all(refreshes)
  }

  const openDetails = (claim: AdminTestnetFaucetClaim) => {
    setSelectedClaim(claim)
    details.onOpen()
  }

  const copyToClipboard = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      toast.success({ title: t('common.copySuccess') })
    } catch {
      toast.error({ title: t('common.copyFailed') })
    }
  }

  const summary = summaryQuery.data
  const claims = claimsQuery.data?.claims ?? []

  return (
    <VStack align="stretch" spacing={4}>
      {summaryQuery.error && (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {summaryQuery.error instanceof Error
            ? summaryQuery.error.message
            : t('common.operationFailed')}
        </Alert>
      )}

      {summary ? (
        <SimpleGrid columns={{ base: 2, md: 3, xl: 5 }} spacing={3}>
          {[
            { key: 'totalClaims', value: summary.claims.total },
            { key: 'last24h', value: summary.claims.last24h },
            { key: 'processing', value: summary.claims.byStatus.processing },
            { key: 'failed', value: summary.claims.byStatus.failed },
          ].map(metric => (
            <Card key={metric.key} borderRadius="md" variant="outline">
              <CardBody py={2} px={3}>
                <HStack justify="space-between" align="center" minH="28px" spacing={3}>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.600" noOfLines={1}>
                    {t(`monitoring.testnetFaucet.metrics.${metric.key}`)}
                  </Text>
                  <Text fontSize="lg" fontWeight="bold" lineHeight="1">
                    {metric.value}
                  </Text>
                </HStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>
      ) : summaryQuery.isLoading ? (
        <HStack justify="center" py={6}>
          <Spinner size="sm" />
          <Text fontSize="sm">{t('common.loading')}</Text>
        </HStack>
      ) : null}

      <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
        <Text fontWeight="semibold">{t('monitoring.testnetFaucet.claims.title')}</Text>
        <RefreshIconButton
          label={t('common.refresh')}
          onClick={handleRefresh}
          isLoading={
            summaryQuery.isFetching
            || claimsQuery.isFetching
            || itemsQuery.isFetching
          }
        />
      </HStack>

      <HStack align="end" spacing={3} flexWrap="wrap">
        <FormControl w="160px" flexShrink={0}>
          <FormLabel fontSize="xs">
            {t('monitoring.testnetFaucet.filters.package')}
          </FormLabel>
          <DropdownSelect
            size="sm"
            value={packageType}
            options={packageOptions}
            onChange={value => {
              setPackageType(value)
              setPage(1)
            }}
          />
        </FormControl>
        <FormControl w="160px" flexShrink={0}>
          <FormLabel fontSize="xs">
            {t('monitoring.testnetFaucet.filters.status')}
          </FormLabel>
          <DropdownSelect
            size="sm"
            value={claimStatus}
            options={statusOptions}
            onChange={value => {
              setClaimStatus(value)
              setPage(1)
            }}
          />
        </FormControl>
        <FormControl w="260px" flexShrink={0}>
          <FormLabel fontSize="xs">
            {t('monitoring.testnetFaucet.filters.userEmail')}
          </FormLabel>
          <Input
            size="sm"
            value={userEmail}
            onChange={event => {
              setUserEmail(event.target.value)
              setPage(1)
            }}
          />
        </FormControl>
        <FormControl w="280px" flexShrink={0}>
          <FormLabel fontSize="xs">
            {t('monitoring.testnetFaucet.filters.recipient')}
          </FormLabel>
          <Input
            size="sm"
            value={recipientAddress}
            onChange={event => {
              setRecipientAddress(event.target.value)
              setPage(1)
            }}
          />
        </FormControl>
      </HStack>

      {claimsQuery.error ? (
        <Alert status="error" borderRadius="md">
          <AlertIcon />
          {claimsQuery.error instanceof Error
            ? claimsQuery.error.message
            : t('common.operationFailed')}
        </Alert>
      ) : claimsQuery.isLoading ? (
        <Box py={10} textAlign="center">
          <Spinner size="md" />
        </Box>
      ) : claims.length === 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          {t('monitoring.testnetFaucet.claims.empty')}
        </Alert>
      ) : (
        <TableContainer borderWidth="1px" borderRadius="md">
          <Table size="sm">
            <Thead>
              <Tr>
                <Th>{t('monitoring.testnetFaucet.claims.columns.user')}</Th>
                <Th>{t('monitoring.testnetFaucet.claims.columns.package')}</Th>
                <Th>{t('monitoring.testnetFaucet.claims.columns.recipient')}</Th>
                <Th>{t('monitoring.testnetFaucet.claims.columns.status')}</Th>
                <Th>{t('monitoring.testnetFaucet.claims.columns.items')}</Th>
                <Th>{t('monitoring.testnetFaucet.claims.columns.updated')}</Th>
                <Th textAlign="right">
                  {t('monitoring.testnetFaucet.claims.columns.actions')}
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {claims.map(claim => {
                const recipientExplorerUrl = claim.explorerChainCode
                  ? getExplorerAddressUrl(claim.explorerChainCode, claim.recipientAddress)
                  : undefined

                return (
                  <Tr key={claim.id}>
                    <Td>
                      <Code fontSize="xs">{claim.userEmail ?? claim.userId}</Code>
                    </Td>
                    <Td>{t(`monitoring.testnetFaucet.packages.${claim.packageType}`)}</Td>
                    <Td>
                      <HStack spacing={1} maxW="100%">
                        <Code fontSize="xs" flex="1" title={claim.recipientAddress}>
                          {truncateHash(claim.recipientAddress, 8, 6)}
                        </Code>
                        <Tooltip label={t('common.copy')}>
                          <IconButton
                            aria-label={t('common.copy')}
                            icon={<FiCopy />}
                            size="xs"
                            variant="ghost"
                            onClick={() => void copyToClipboard(claim.recipientAddress)}
                          />
                        </Tooltip>
                        {recipientExplorerUrl && (
                          <Tooltip label={t('common.openExplorer')}>
                            <IconButton
                              aria-label={t('common.openExplorer')}
                              icon={<FiExternalLink />}
                              size="xs"
                              variant="ghost"
                              onClick={() => {
                                window.open(
                                  recipientExplorerUrl,
                                  '_blank',
                                  'noopener,noreferrer'
                                )
                              }}
                            />
                          </Tooltip>
                        )}
                      </HStack>
                    </Td>
                    <Td>
                      <Badge colorScheme={claimStatusColor(claim.status)} variant="subtle">
                        {t(`monitoring.testnetFaucet.statuses.${claim.status}`)}
                      </Badge>
                    </Td>
                    <Td>
                      <Text fontSize="sm">
                        {claim.itemSummary.confirmed}/{claim.itemSummary.total}
                      </Text>
                      <Text fontSize="xs" color="gray.500">
                        {t('monitoring.testnetFaucet.claims.txCount', {
                          count: claim.itemSummary.txCount,
                        })}
                      </Text>
                    </Td>
                    <Td><DateTimeStack value={claim.updatedAt} /></Td>
                    <Td textAlign="right">
                      <Tooltip label={t('common.view')}>
                        <IconButton
                          aria-label={t('common.view')}
                          icon={<FiEye />}
                          size="xs"
                          variant="ghost"
                          onClick={() => openDetails(claim)}
                        />
                      </Tooltip>
                    </Td>
                  </Tr>
                )
              })}
            </Tbody>
          </Table>
        </TableContainer>
      )}

      {claimsQuery.data && claimsQuery.data.pagination.total > 0 && (
        <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
          <HStack spacing={4} flexWrap="wrap">
            <Text fontSize="sm" color="gray.500">
              {t('common.pagination.pageOf', {
                current: claimsQuery.data.pagination.page,
                total: claimsQuery.data.pagination.totalPages,
                defaultValue:
                  `Page ${claimsQuery.data.pagination.page}`
                  + ` of ${claimsQuery.data.pagination.totalPages}`,
              })}
            </Text>
            <HStack spacing={2}>
              <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">
                {t('common.pagination.pageSize')}
              </Text>
              <Box w="84px">
                <DropdownSelect
                  size="xs"
                  value={String(pageSize)}
                  options={pageSizeOptions}
                  onChange={value => {
                    setPageSize(Number(value))
                    setPage(1)
                  }}
                />
              </Box>
            </HStack>
          </HStack>
          <HStack>
            <Button size="sm" onClick={() => setPage(value => value - 1)} isDisabled={page <= 1}>
              {t('common.previous')}
            </Button>
            <Button
              size="sm"
              onClick={() => setPage(value => value + 1)}
              isDisabled={page >= claimsQuery.data.pagination.totalPages}
            >
              {t('common.next')}
            </Button>
          </HStack>
        </HStack>
      )}

      <Modal isOpen={details.isOpen} onClose={details.onClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('monitoring.testnetFaucet.items.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {itemsQuery.error ? (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {itemsQuery.error instanceof Error
                  ? itemsQuery.error.message
                  : t('common.operationFailed')}
              </Alert>
            ) : itemsQuery.isLoading ? (
              <Box py={8} textAlign="center">
                <Spinner size="md" />
              </Box>
            ) : (itemsQuery.data?.items.length ?? 0) === 0 ? (
              <Alert status="info" borderRadius="md">
                <AlertIcon />
                {t('monitoring.testnetFaucet.items.empty')}
              </Alert>
            ) : (
              <TableContainer borderWidth="1px" borderRadius="md">
                <Table size="sm">
                  <Thead>
                    <Tr>
                      <Th>{t('monitoring.testnetFaucet.items.columns.chain')}</Th>
                      <Th>{t('monitoring.testnetFaucet.items.columns.token')}</Th>
                      <Th>{t('monitoring.testnetFaucet.items.columns.amount')}</Th>
                      <Th>{t('monitoring.testnetFaucet.items.columns.status')}</Th>
                      <Th>{t('monitoring.testnetFaucet.items.columns.attempts')}</Th>
                      <Th>{t('monitoring.testnetFaucet.items.columns.tx')}</Th>
                      <Th>{t('monitoring.testnetFaucet.items.columns.error')}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {itemsQuery.data?.items.map(item => {
                      const txExplorerUrl = item.txHash
                        ? getExplorerTxUrl(item.chainCode ?? item.chainId, item.txHash)
                        : undefined

                      return (
                        <Tr key={item.id}>
                          <Td>
                            <Text fontSize="sm">{item.chainName ?? item.chainCode}</Text>
                          </Td>
                          <Td>
                            <Text fontSize="sm" fontWeight="medium">
                              {item.tokenSymbol}
                            </Text>
                            <Code fontSize="xs">{truncateHash(item.tokenAddress, 8, 6)}</Code>
                          </Td>
                          <Td>
                            <Code fontSize="xs">{item.amountRaw}</Code>
                            <Text fontSize="xs" color="gray.500">
                              {t('monitoring.testnetFaucet.items.decimals', {
                                count: item.decimals,
                              })}
                            </Text>
                          </Td>
                          <Td>
                            <Badge colorScheme={itemStatusColor(item.status)} variant="subtle">
                              {t(`monitoring.testnetFaucet.itemStatuses.${item.status}`)}
                            </Badge>
                          </Td>
                          <Td>{item.attemptCount}</Td>
                          <Td>
                            {item.txHash ? (
                              <HStack spacing={1}>
                                <Code fontSize="xs" title={item.txHash}>
                                  {truncateHash(item.txHash, 8, 6)}
                                </Code>
                                <Tooltip label={t('common.copy')}>
                                  <IconButton
                                    aria-label={t('common.copy')}
                                    icon={<FiCopy />}
                                    size="xs"
                                    variant="ghost"
                                    onClick={() => void copyToClipboard(item.txHash ?? '')}
                                  />
                                </Tooltip>
                                {txExplorerUrl && (
                                  <Tooltip label={t('common.openExplorer')}>
                                    <IconButton
                                      aria-label={t('common.openExplorer')}
                                      icon={<FiExternalLink />}
                                      size="xs"
                                      variant="ghost"
                                      onClick={() => {
                                        window.open(
                                          txExplorerUrl,
                                          '_blank',
                                          'noopener,noreferrer'
                                        )
                                      }}
                                    />
                                  </Tooltip>
                                )}
                              </HStack>
                            ) : (
                              t('common.na')
                            )}
                          </Td>
                          <Td>
                          {item.errorCode || item.errorMessage ? (
                            <VStack align="start" spacing={0}>
                              {item.errorCode && <Code fontSize="xs">{item.errorCode}</Code>}
                              {item.errorMessage && (
                                <Text fontSize="xs" color="red.500" maxW="260px" noOfLines={2}>
                                  {item.errorMessage}
                                </Text>
                              )}
                            </VStack>
                          ) : (
                            t('common.na')
                          )}
                          </Td>
                        </Tr>
                      )
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </VStack>
  )
}
